/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/ChecklistItemRow.tsx
 * FILE_ID: JOBHUB-QA-CHECKLIST-ITEM-ROW-001
 * PURPOSE: One Run-mode checklist row and its on-site reading capture.
 * WHY: Moved VERBATIM out of JobQaExecution.tsx, which reached the 500-line
 *      ceiling. Behaviour is pinned by jobQaSetup.pin.test.ts, which now reads
 *      this file for the reading-capture assertions. Do not re-author.
 * LANDMINE: ReadingCapture resets its local value/unit whenever the item's
 *      captured_* props change; commit() is a no-op when nothing changed so
 *      blur cannot spam the API.
 * RELATED:
 * - JOBHUB-JOBS-QA-EXECUTION-001 (the panel that renders these rows)
 */
import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2, Thermometer } from 'lucide-react'
import { QaItemStatusButtons, QaOwnerVerification, qaStatusMeta, type QaNote } from '@quotech/ui'

import type { createQaApi, QaEvidence, QaHoldPoint, QaItemInstance, QaItemStatus } from '../../qa/qaApi'
import { QaItemEvidenceStrip, splitQaEvidence } from '../../qa/QaItemEvidenceStrip'
import { QaPhotoSourceMenu } from '../../qa/QaPhotoSourceMenu'
import { assetForDeviceFile } from '../../qa/qaJobLibrary'
import { QaRunItemActions } from '../../qa/QaRunItemActions'
import { QaHoldPointSignoff } from './hold-points/QaHoldPointSignoff'
import { QaNotesDisclosure } from './QaNotesDisclosure'
import { useQaRunTextChecks } from './QaRunTextChecks'
import { qaErrorMessage } from './qaTabSelectors'
import { forgetEvidencePhoto, rememberEvidencePhoto } from './useQaEvidenceUrls'

function RowStatusIcon({ status }: { status: string }) {
  const meta = qaStatusMeta(status === 'pass' ? 'done' : status)
  const Icon = meta.icon
  return <Icon className={`h-5 w-5 ${meta.icon_class}`} aria-hidden />
}

/**
 * On-site measurement capture for checklist items flagged
 * evidence_rules.requires_reading (substrate/air temperature, RH, dew point,
 * DFT). Value + unit auto-save on blur; the recorded reading is shown back.
 */
export function ReadingCapture({
  item,
  api,
  onMutated,
}: {
  item: QaItemInstance
  api: ReturnType<typeof createQaApi>
  onMutated: () => void
}) {
  const [value, setValue] = useState(item.captured_value ?? '')
  const [unit, setUnit] = useState(item.captured_unit ?? '')
  const [saving, setSaving] = useState(false)
  useEffect(() => {
    setValue(item.captured_value ?? '')
    setUnit(item.captured_unit ?? '')
  }, [item.captured_value, item.captured_unit])

  const commit = async () => {
    const v = value.trim()
    const u = unit.trim()
    if (v === (item.captured_value ?? '').trim() && u === (item.captured_unit ?? '').trim()) return
    setSaving(true)
    try {
      await api.setReading(item.id, v, u)
      onMutated()
    } catch {
      setValue(item.captured_value ?? '')
      setUnit(item.captured_unit ?? '')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mt-2 flex items-center gap-2">
      <Thermometer className="h-3.5 w-3.5 text-status-info shrink-0" aria-hidden />
      <span className="text-[11px] text-muted-foreground">Reading</span>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => void commit()}
        placeholder="value"
        aria-label={`Reading value for ${item.title}`}
        className="w-20 rounded-md border border-border bg-background px-2 py-1 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
      />
      <input
        type="text"
        value={unit}
        onChange={(e) => setUnit(e.target.value)}
        onBlur={() => void commit()}
        placeholder="°C"
        aria-label={`Reading unit for ${item.title}`}
        className="w-16 rounded-md border border-border bg-background px-2 py-1 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
      />
      {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden />}
      {!saving && item.captured_value ? (
        <span className="text-[11px] text-status-good">
          recorded {item.captured_value}
          {item.captured_unit ? ` ${item.captured_unit}` : ''}
        </span>
      ) : null}
    </div>
  )
}

export function ChecklistItemRow({
  item,
  evidence,
  jobId,
  areaId,
  api,
  onMutated,
  onSetStatus,
  statusPending,
  selection,
  onOpenItp,
  onWriteError,
  signoff,
  checksLocked = false,
}: {
  item: QaItemInstance
  evidence: QaEvidence[]
  jobId: number
  areaId: string
  api: ReturnType<typeof createQaApi>
  onMutated: () => void
  onSetStatus: (itemId: string, status: QaItemStatus) => void
  statusPending: boolean
  selection: { selected: boolean; onToggle: () => void }
  onOpenItp?: () => void
  onWriteError?: (message: string) => void
  /** The area's hold points and whether this reader may sign them. */
  signoff?: { holdPoints: readonly QaHoldPoint[]; canSign: boolean }
  checksLocked?: boolean
}) {
  const queryClient = useQueryClient()
  const [uploading, setUploading] = useState(false)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [noteError, setNoteError] = useState<string | null>(null)
  const checks = useQaRunTextChecks({
    itemId: item.id,
    requirementText: item.requirement_text,
    criteriaText: item.acceptance_criteria,
    lineChecks: item.line_checks,
    locked: checksLocked,
    onSave: async (kind, indexes) => {
      await api.setLineChecks(item.id, kind, indexes)
      onMutated()
    },
  })

  const { photos, documents } = splitQaEvidence(evidence)
  const notes: QaNote[] = evidence
    .filter((ev) => ev.type === 'note')
    .map((ev) => ({
      id: ev.id,
      text: ev.note_text ?? '',
      authorName: ev.captured_by_name,
      at: ev.captured_at ?? '',
    }))

  const failWrite = (error: unknown, fallback: string) => {
    const message = qaErrorMessage(error, fallback)
    setNoteError(message)
    onWriteError?.(message)
  }

  const onAddNote = async (text: string) => {
    setNoteError(null)
    try {
      await api.addNoteEvidence({ areaId, itemInstanceId: item.id, noteText: text })
      onMutated()
    } catch (error) {
      failWrite(error, 'Could not save that note.')
    }
  }

  const onDeleteNote = async (noteId: string) => {
    setNoteError(null)
    try {
      await api.deleteEvidence(noteId)
      onMutated()
    } catch (error) {
      failWrite(error, 'Could not delete that note.')
    }
  }

  const attachPhoto = async (assetId: string) => {
    const created = await api.addPhotoEvidence({ areaId, itemInstanceId: item.id, assetId, description: item.title })
    // Reused from: packages/job-hub/src/jobs/tabs/job-qa/useQaEvidenceUrls.ts (rememberEvidencePhoto)
    rememberEvidencePhoto(queryClient, areaId, created)
    onMutated()
  }

  const attachDocument = async (assetId: string, filename?: string) => {
    await api.addDocumentEvidence({
      areaId,
      itemInstanceId: item.id,
      assetId,
      description: filename || item.title,
    })
    onMutated()
  }

  const runAttach = async (attach: () => Promise<void>, failure: string) => {
    setUploading(true)
    setPhotoError(null)
    try {
      await attach()
    } catch (error) {
      setPhotoError(qaErrorMessage(error, failure))
    } finally {
      setUploading(false)
    }
  }

  const onPickPhotoFile = (file: File) =>
    runAttach(async () => {
      // Reused from: packages/job-hub/src/jobs/qa/qaInspectionApi.ts (uploadFile) when no library match
      const assetId = await assetForDeviceFile(file, () => api.listQaLibrary(jobId), (next) => api.uploadFile(next, jobId))
      await attachPhoto(assetId)
    }, 'Upload failed. Please try again.')

  const onPickPhotoAsset = (assetId: string) =>
    runAttach(() => attachPhoto(assetId), 'Could not attach that file. Please try again.')

  const onPickDocumentFile = (file: File) =>
    runAttach(async () => {
      // Reused from: packages/job-hub/src/jobs/qa/qaInspectionApi.ts (uploadFile) when no library match
      const assetId = await assetForDeviceFile(file, () => api.listQaLibrary(jobId), (next) => api.uploadFile(next, jobId))
      await attachDocument(assetId, file.name)
    }, 'Upload failed. Please try again.')

  const onPickDocumentAsset = (assetId: string, filename?: string) =>
    runAttach(() => attachDocument(assetId, filename), 'Could not attach that file. Please try again.')

  const onDeletePhoto = async (evidenceId: string) => {
    try {
      await api.deleteEvidence(evidenceId)
      // Reused from: packages/job-hub/src/jobs/tabs/job-qa/useQaEvidenceUrls.ts (rememberEvidencePhoto)
      forgetEvidencePhoto(queryClient, areaId, evidenceId)
      onMutated()
    } catch (error) {
      setPhotoError(qaErrorMessage(error, 'Could not remove that photo.'))
    }
  }

  return (
    <li
      // Selection = blue border, not a background wash (mockup Select rules).
      className={selection.selected
        ? 'rounded-lg border border-primary ring-1 ring-primary/30 p-3'
        : 'p-3'}
      data-selected={selection.selected || undefined}
    >
      <div className="flex items-start gap-3">
        <div className="flex shrink-0 flex-col items-center gap-1.5">
          <input
            type="checkbox"
            checked={selection.selected}
            onChange={selection.onToggle}
            aria-label={`Select ${item.item_number} ${item.title}`}
            data-testid={`qa-item-select-${item.id}`}
            className="mt-1.5 h-3.5 w-3.5 shrink-0 rounded border-border"
          />
          <RowStatusIcon status={item.status} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground tabular-nums">{item.display_number ?? item.item_number}</span>
            {onOpenItp ? (
              <button
                type="button"
                onClick={onOpenItp}
                className="text-left text-sm font-medium text-foreground hover:text-primary"
              >
                {item.title}
              </button>
            ) : (
              <p className="truncate text-sm font-medium text-foreground">{item.title}</p>
            )}
            {item.has_hold_point && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-status-warn-muted text-status-warn">
                Hold point
              </span>
            )}
          </div>
          {item.acceptance_criteria && (
            <p className="text-xs text-muted-foreground mt-0.5">{item.acceptance_criteria}</p>
          )}
          {item.product && (
            <p className="mt-0.5 truncate text-xs font-medium text-primary">{item.product}</p>
          )}

          <div className="mt-2">
            <QaItemStatusButtons
              currentStatus={item.status}
              disabled={statusPending}
              onSelect={(status) => onSetStatus(item.id, status)}
            />
          </div>

          {/* Owner-verified completion. Sits under the check's own criteria and
              above its evidence, in normal flow, so confirming reflows the row
              below rather than covering it. */}
          <QaOwnerVerification
            className="mt-1.5"
            targetLabel={`${item.item_number} ${item.title}`}
            owner_verified={item.owner_verified}
            owner_verified_at={item.owner_verified_at}
            owner_verified_by_name={item.owner_verified_by_name}
            can_verify_completion={item.can_verify_completion}
            onVerify={async (verified) => {
              await api.verifyItemCompletion(item.id, verified)
              onMutated()
            }}
          />
          <QaHoldPointSignoff
            item={item}
            areaId={areaId}
            jobId={jobId}
            holdPoints={signoff?.holdPoints ?? []}
            canSign={Boolean(signoff?.canSign)}
            api={api}
            onMutated={onMutated}
            onWriteError={onWriteError}
          />
          <QaRunItemActions
            item={item}
            areaId={areaId}
            api={api}
            onMutated={onMutated}
            showEditActions={false}
          />

          {/* On-site measurement capture (temperature, RH, dew point, DFT…) */}
          {!!item.evidence_rules?.requires_reading && (
            <ReadingCapture item={item} api={api} onMutated={onMutated} />
          )}

          <QaItemEvidenceStrip photos={photos} documents={documents} onDelete={onDeletePhoto} />

          {(photoError || noteError) && (
            <p role="alert" className="text-xs text-destructive mt-1">{noteError || photoError}</p>
          )}

          <QaNotesDisclosure
            itemTitle={item.title}
            notes={notes}
            onAdd={onAddNote}
            onDelete={onDeleteNote}
            emptyLabel="No notes on this item yet."
            draftKey={`${areaId}:${item.id}`}
          />
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <QaPhotoSourceMenu
            busy={uploading}
            listQDriveFiles={api.listQDriveFiles}
            listLibraryFiles={() => api.listQaLibrary(jobId)}
            onPickFile={onPickPhotoFile}
            onPickAsset={onPickPhotoAsset}
          />
          <QaPhotoSourceMenu
            mode="file"
            busy={uploading}
            listQDriveFiles={api.listQDriveFiles}
            listLibraryFiles={() => api.listQaLibrary(jobId)}
            onPickFile={onPickDocumentFile}
            onPickAsset={onPickDocumentAsset}
          />
          {/* Reused from: packages/job-hub/src/jobs/tabs/job-qa/ChecklistItemRow.tsx (QaPhotoSourceMenu column). */}
          {checks.buttons}
        </div>
      </div>
      {checks.panel}
    </li>
  )
}
